import type { Position } from '@/types/game';
import type { CharacterProp } from '@/types/props';
import { motion } from 'motion/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { OBSTACLE_HEIGHTS } from '../GameBoard';
import Prop3D from './Prop3D';

// Example custom character config:
// const customConfig: Partial<CharacterConfig> = {
//   colors: {
//     faces: {
//       front: 'linear-gradient(135deg, #00ff00 0%, #00cc00 50%, #009900 100%)',
//       back: 'linear-gradient(135deg, #0099ff 0%, #0066cc 50%, #003399 100%)',
//     },
//   },
//   face: {
//     eyes: {
//       color: '#ff0000',
//       size: '40%',
//     },
//   },
// };
// Then pass it as: <Character3D {...props} config={customConfig} />

// Deep partial type helper
type DeepPartial<T> = T extends object
  ? {
      [P in keyof T]?: DeepPartial<T[P]>;
    }
  : T;

export interface CharacterConfig {
  sizes: {
    // Main character size as percentage of cell (0-1, default 0.84 = 84% of cell)
    characterSize: number;
    // Cube size as percentage of character (0-1, default 0.91 = 91% of character)
    cubeSize: number;
    // Shadow properties
    shadow: {
      width: number; // Shadow width as percentage of character (0-1, default 0.8)
      height: number; // Shadow height as percentage of character (0-1, default 0.2)
      offsetY: number; // Y offset as percentage of cell (0-1, default 0.1)
    };
  };
  face: {
    eyes: {
      containerWidth: string; // '60%'
      containerMarginTop: string; // '10%'
      gap: string; // '25%'
      size: string; // '30%' of container
      color: string; // '#000'
      shine: {
        size: string; // '40%' of eye
        position: { top: string; left: string }; // '20%', '25%'
        color: string; // 'rgba(255, 255, 255, 0.8)'
      };
    };
    mouth: {
      width: string; // '35%'
      heights: {
        neutral: string; // '12%'
        happy: string; // '20%'
        shocked: string; // '25%'
      };
      color: string; // 'rgba(0, 0, 0, 0.3)'
    };
  };
  colors: {
    faces: {
      front: string; // Gradient
      back: string;
      top: string;
      bottom: string;
      left: string;
      right: string;
    };
    effects: {
      portal: string; // Portal gradient
      bubble: string; // Bubble gradient
      shadow: string; // Shadow gradient
    };
    borders: {
      face: string; // '2px solid rgba(0, 0, 0, 0.2)'
      bubble: string; // '1px solid rgba(100, 200, 255, 0.3)'
    };
  };
  backFace: {
    symbol: string; // text symbol for back face
    sizeScale: number; // Relative to cubeSize (0.5)
    opacity: number; // 0.3
  };
  animations: {
    idle: IdleAnimation[]; // Available idle animations
    blinkInterval: { min: number; max: number }; // 3000, 2000
    idleInterval: { min: number; max: number }; // 4000, 3000
  };
  effects: {
    bubbles: {
      count: number; // 3
      size: string; // '20%'
      positions: { min: number; max: number }; // 40, 20
    };
    portal: {
      size: string; // '150%'
      offset: string; // '-25%'
    };
  };
}

interface Character3DProps {
  position: Position;
  direction: 'up' | 'down' | 'left' | 'right' | null;
  isMoving: boolean;
  cellSize: number;
  hasCollided?: boolean;
  gameState?: 'playing' | 'won' | 'lost' | 'entering' | 'exiting';
  onEntranceComplete?: () => void;
  onTap?: () => void;
  currentTileType?: string;
  config?: DeepPartial<CharacterConfig>;
  equippedProps?: CharacterProp[];
}

type IdleAnimation = 'breathe' | 'wave' | 'spin' | 'bounce' | 'lookAround' | 'dance';

// Default character configuration
export const defaultCharacterConfig: CharacterConfig = {
  sizes: {
    characterSize: 0.84,
    cubeSize: 0.91,
    shadow: {
      width: 0.8,
      height: 0.5,
      offsetY: 0.1,
    },
  },
  face: {
    eyes: {
      containerWidth: '60%',
      containerMarginTop: '3%',
      gap: '25%',
      size: '30%',
      color: '#000',
      shine: {
        size: '40%',
        position: { top: '20%', left: '25%' },
        color: 'rgba(255, 255, 255, 0.8)',
      },
    },
    mouth: {
      width: '35%',
      heights: {
        neutral: '12%',
        happy: '20%',
        shocked: '25%',
      },
      color: 'rgba(0, 0, 0, 0.3)',
    },
  },
  colors: {
    faces: {
      front: 'linear-gradient(135deg, #ff8a95 0%, #ff6b7a 50%, #ee4d5f 100%)',
      back: 'linear-gradient(135deg, #dd5566 0%, #cc4455 50%, #aa3344 100%)',
      top: 'linear-gradient(135deg, #ffaab5 0%, #ff8a95 50%, #ff6b7a 100%)',
      bottom: 'linear-gradient(135deg, #cc4455 0%, #aa3344 50%, #882233 100%)',
      left: 'linear-gradient(135deg, #ff7a8a 0%, #ee5a6f 50%, #dd4455 100%)',
      right: 'linear-gradient(135deg, #ff7a8a 0%, #ee5a6f 50%, #dd4455 100%)',
    },
    effects: {
      portal: 'radial-gradient(circle, rgba(100, 200, 255, 0.8) 0%, transparent 60%)',
      bubble:
        'radial-gradient(circle, rgba(100, 200, 255, 0.6) 0%, rgba(100, 200, 255, 0.2) 50%, transparent 70%)',
      shadow: 'radial-gradient(ellipse, rgba(0, 0, 0, 0.3) 0%, transparent 70%)',
    },
    borders: {
      face: '2px solid rgba(0, 0, 0, 0.2)',
      bubble: '1px solid rgba(100, 200, 255, 0.3)',
    },
  },
  backFace: {
    symbol: '+',
    sizeScale: 0.5,
    opacity: 0.3,
  },
  animations: {
    idle: ['breathe', 'bounce', 'lookAround', 'dance'],
    blinkInterval: { min: 3000, max: 2000 },
    idleInterval: { min: 4000, max: 3000 },
  },
  effects: {
    bubbles: {
      count: 3,
      size: '20%',
      positions: { min: 40, max: 20 },
    },
    portal: {
      size: '150%',
      offset: '-25%',
    },
  },
};

const Character3D: React.FC<Character3DProps> = ({
  position,
  direction,
  isMoving,
  cellSize,
  hasCollided = false,
  gameState = 'playing',
  onEntranceComplete,
  onTap,
  currentTileType = 'empty',
  config = {},
  equippedProps = [],
}) => {
  // Merge provided config with defaults
  const characterConfig = useMemo(() => {
    const mergeDeep = (target: any, source: any): any => {
      const output = { ...target };
      for (const key in source) {
        if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
          output[key] = mergeDeep(target[key] || {}, source[key]);
        } else {
          output[key] = source[key];
        }
      }
      return output;
    };
    return mergeDeep(defaultCharacterConfig, config);
  }, [config]);

  const [isBlinking, setIsBlinking] = useState(false);
  const [currentIdleAnimation, setCurrentIdleAnimation] = useState<IdleAnimation>('breathe');
  const [expression, setExpression] = useState<'neutral' | 'happy' | 'shocked' | 'sad' | 'wink'>(
    'neutral'
  );
  const [entranceHops, setEntranceHops] = useState(0);
  const [exitHops, setExitHops] = useState(0);
  const [isInvisible, setIsInvisible] = useState(gameState === 'entering');
  const [previousPositions, setPreviousPositions] = useState<
    Array<{ position: Position; id: number }>
  >([]);
  const [isJiggling, setIsJiggling] = useState(false);
  const wasMovingRef = useRef(false);

  const characterSize = cellSize * characterConfig.sizes.characterSize;
  const cubeSize = characterSize * characterConfig.sizes.cubeSize; // Slightly smaller to show jiggle
  const bubbleIdRef = useRef(0);
  const prevPositionRef = useRef<Position>(position);

  // Track position changes for bubble trail and jiggle effect
  useEffect(() => {
    const hasPositionChanged =
      prevPositionRef.current[0] !== position[0] || prevPositionRef.current[1] !== position[1];

    if (hasPositionChanged && isMoving && direction) {
      // Add bubble at previous position
      const currentBubbleId = bubbleIdRef.current++;
      setPreviousPositions(prev => [
        ...prev,
        {
          position: [...prevPositionRef.current],
          id: currentBubbleId,
        },
      ]);

      // Clean up old bubbles after animation completes
      setTimeout(() => {
        setPreviousPositions(prev => prev.filter(p => p.id !== currentBubbleId));
      }, 800);

      prevPositionRef.current = position;
    }

    // Jiggle effect when stopping
    if (wasMovingRef.current && !isMoving) {
      setIsJiggling(true);
      setTimeout(() => setIsJiggling(false), 600);
    }
    wasMovingRef.current = isMoving;
  }, [position, isMoving, direction]);

  // Get elevation based on current tile type
  const elevation = useMemo(() => {
    const tileHeight = OBSTACLE_HEIGHTS[currentTileType as keyof typeof OBSTACLE_HEIGHTS] || 0;
    return cellSize * tileHeight;
  }, [currentTileType, cellSize]);

  // Consolidated animation loop for blink and idle - replaces multiple setInterval/setTimeout
  useEffect(() => {
    if (gameState !== 'playing') {
      setCurrentIdleAnimation('breathe');
      return;
    }

    let rafId: number;
    let lastBlinkTime = performance.now();
    let nextBlinkDelay = characterConfig.animations.blinkInterval.min + Math.random() * characterConfig.animations.blinkInterval.max;
    let blinkEndTime = 0;
    let lastIdleTime = performance.now();
    let nextIdleDelay = characterConfig.animations.idleInterval.min + Math.random() * characterConfig.animations.idleInterval.max;

    const tick = (now: number) => {
      // Blink logic
      if (blinkEndTime > 0) {
        if (now >= blinkEndTime) {
          setIsBlinking(false);
          blinkEndTime = 0;
          lastBlinkTime = now;
          nextBlinkDelay = characterConfig.animations.blinkInterval.min + Math.random() * characterConfig.animations.blinkInterval.max;
        }
      } else if (now - lastBlinkTime >= nextBlinkDelay) {
        setIsBlinking(true);
        blinkEndTime = now + 150;
      }

      // Idle animation logic
      if (!isMoving && now - lastIdleTime >= nextIdleDelay) {
        const animations = characterConfig.animations.idle;
        const randomAnim = animations[Math.floor(Math.random() * animations.length)];
        setCurrentIdleAnimation(randomAnim);
        lastIdleTime = now;
        nextIdleDelay = characterConfig.animations.idleInterval.min + Math.random() * characterConfig.animations.idleInterval.max;
      } else if (isMoving) {
        setCurrentIdleAnimation('breathe');
        lastIdleTime = now;
      }

      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [gameState, isMoving, characterConfig.animations]);

  // Expression based on state
  useEffect(() => {
    if (gameState === 'won') {
      setExpression('happy');
    } else if (gameState === 'lost') {
      // Death animation sequence: impact -> deflate -> fade
      setExpression('shocked');
      setDeathPhase('impact');

      const t1 = setTimeout(() => {
        setExpression('sad');
        setDeathPhase('deflate');
      }, 200);

      const t2 = setTimeout(() => {
        setDeathPhase('fade');
      }, 700);

      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    } else if (hasCollided) {
      setExpression('shocked');
      setTimeout(() => setExpression('neutral'), 1000);
    } else {
      // Reset death phase when returning to normal states
      setDeathPhase('none');
    }
  }, [gameState, hasCollided]);
  
  // Handle entrance animation sequence
  useEffect(() => {
    if (gameState === 'entering') {
      setIsInvisible(true);
      setEntranceHops(0);
      
      // Start entrance sequence
      setTimeout(() => {
        setIsInvisible(false); // Appear
        
        // First hop
        setTimeout(() => {
          setEntranceHops(1);
          setTimeout(() => setEntranceHops(0), 300);
        }, 200);
        
        // Second hop 
        setTimeout(() => {
          setEntranceHops(2);
          setTimeout(() => {
            setEntranceHops(0);
            // Complete entrance after hops
            setTimeout(() => onEntranceComplete?.(), 200);
          }, 300);
        }, 600);
        
      }, 500); // Brief delay before appearing
    }
  }, [gameState, onEntranceComplete]);
  
  // Fly-away animation state
  const [flyAwayPhase, setFlyAwayPhase] = useState<'idle' | 'spin' | 'launch' | 'gone'>('idle');

  // Death animation state
  const [deathPhase, setDeathPhase] = useState<'none' | 'impact' | 'deflate' | 'fade'>('none');

  // Teleport animation state
  const [teleportPhase, setTeleportPhase] = useState<'idle' | 'landing' | 'suckIn' | 'grow'>('idle');

  // Handle exit animation sequence - fly away
  useEffect(() => {
    if (gameState === 'exiting') {
      setExitHops(0);
      setExpression('happy');
      setFlyAwayPhase('idle');

      // Quick celebration hop
      setTimeout(() => {
        setExitHops(2);
        setTimeout(() => setExitHops(0), 300);
      }, 200);

      // Start spinning
      setTimeout(() => {
        setFlyAwayPhase('spin');
      }, 600);

      // Launch upward
      setTimeout(() => {
        setFlyAwayPhase('launch');
      }, 1000);

      // Fully gone
      setTimeout(() => {
        setFlyAwayPhase('gone');
        setIsInvisible(true);
      }, 1800);
    } else {
      setFlyAwayPhase('idle');
    }
  }, [gameState]);

  // Listen for teleport events and animate landing -> suck-in -> grow-out
  useEffect(() => {
    const teleportTimers: ReturnType<typeof setTimeout>[] = [];
    const handleTeleport = () => {
      // Phase 1: Land on the teleporter platform (500ms)
      setTeleportPhase('landing');

      // Phase 2: Get sucked into the platform (300ms)
      teleportTimers.push(setTimeout(() => {
        setTeleportPhase('suckIn');
      }, 500));

      // Phase 3: Blown out at destination (400ms)
      teleportTimers.push(setTimeout(() => {
        setTeleportPhase('grow');
      }, 800));

      // Return to idle
      teleportTimers.push(setTimeout(() => {
        setTeleportPhase('idle');
      }, 1200));
    };

    window.addEventListener('teleport', handleTeleport);
    return () => {
      window.removeEventListener('teleport', handleTeleport);
      teleportTimers.forEach(clearTimeout);
    };
  }, []);

  // Wobble on tap
  const [wobbleKey, setWobbleKey] = useState(0);
  const handleTap = () => {
    if (gameState !== 'playing' || isMoving) return;
    setWobbleKey(prev => prev + 1);
    onTap?.();
  };

  // Calculate position with elevation
  const characterPosition = useMemo(() => {
    return {
      left: position[0] * cellSize + (cellSize - characterSize) / 2,
      top: position[1] * cellSize + (cellSize - characterSize) / 2 - elevation,
    };
  }, [position, cellSize, characterSize, elevation]);

  return (
    <motion.div
      onClick={handleTap}
      style={{
        position: 'absolute',
        width: characterSize,
        height: characterSize,
        zIndex: 1000,
        cursor: gameState === 'playing' ? 'pointer' : undefined,
      }}
      initial={{
        left: characterPosition.left,
        top: characterPosition.top,
        scale: 1,
        opacity: isInvisible ? 0 : 1,
        y: entranceHops > 0 ? -20 * entranceHops : exitHops > 0 ? -25 * exitHops : 0,
        rotate: 0,
      }}
      animate={{
        left: characterPosition.left,
        top: characterPosition.top,
        scale: flyAwayPhase === 'launch' || flyAwayPhase === 'gone' ? 0.3
          : flyAwayPhase === 'spin' ? 1.2
          : 1,
        opacity: isInvisible ? 0
          : flyAwayPhase === 'launch' ? 0.6
          : flyAwayPhase === 'gone' ? 0
          : 1,
        y: flyAwayPhase === 'launch' ? -300
          : flyAwayPhase === 'gone' ? -500
          : entranceHops > 0 ? -20 * entranceHops
          : exitHops > 0 ? -25 * exitHops
          : 0,
        rotate: flyAwayPhase === 'spin' ? 360
          : flyAwayPhase === 'launch' ? 720
          : flyAwayPhase === 'gone' ? 1080
          : 0,
      }}
      transition={{
        left: {
          type: 'spring',
          stiffness: 300,
          damping: 25,
        },
        top: {
          type: 'spring',
          stiffness: 300,
          damping: 25,
        },
        scale: flyAwayPhase !== 'idle' ? {
          type: 'tween',
          duration: flyAwayPhase === 'launch' ? 0.6 : 0.3,
          ease: 'easeInOut',
        } : {
          type: 'spring',
          stiffness: 200,
          damping: 20,
        },
        opacity: {
          duration: flyAwayPhase === 'launch' ? 0.8 : isInvisible ? 0.5 : 0.3,
        },
        y: flyAwayPhase === 'launch' || flyAwayPhase === 'gone' ? {
          type: 'tween',
          duration: 0.8,
          ease: [0.2, 0.8, 0.2, 1],
        } : {
          type: 'spring',
          stiffness: entranceHops > 0 || exitHops > 0 ? 400 : 200,
          damping: entranceHops > 0 || exitHops > 0 ? 15 : 20,
        },
        rotate: flyAwayPhase !== 'idle' ? {
          type: 'tween',
          duration: flyAwayPhase === 'launch' ? 0.8 : 0.4,
          ease: 'easeInOut',
        } : {
          duration: 0,
        },
      }}
      onAnimationComplete={() => {
        if (gameState === 'entering') {
          onEntranceComplete?.();
        }
      }}
    >
      {/* Portal effect for entrance */}
      {gameState === 'entering' && (
        <motion.div
          style={{
            position: 'absolute',
            width: characterConfig.effects.portal.size,
            height: characterConfig.effects.portal.size,
            left: characterConfig.effects.portal.offset,
            top: characterConfig.effects.portal.offset,
            borderRadius: '50%',
            background: characterConfig.colors.effects.portal,
            pointerEvents: 'none',
          }}
          animate={{ scale: 0, opacity: 0 }}
          initial={{ scale: 2, opacity: 1 }}
          transition={{ duration: 0.8, ease: 'easeInOut' }}
        />
      )}

      {/* Fly-away trail sparkles */}
      {gameState === 'exiting' && (flyAwayPhase === 'spin' || flyAwayPhase === 'launch') && (
        <motion.div
          style={{
            position: 'absolute',
            width: '150%',
            height: '150%',
            left: '-25%',
            top: '-25%',
            pointerEvents: 'none',
          }}
        >
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              style={{
                position: 'absolute',
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: i % 2 === 0 ? '#FFD700' : '#fff',
                boxShadow: `0 0 8px ${i % 2 === 0 ? '#FFD700' : '#64B5F6'}`,
                left: `${30 + Math.cos(i * 1.05) * 40}%`,
                top: `${30 + Math.sin(i * 1.05) * 40}%`,
              }}
              animate={{
                scale: [0, 1.5, 0],
                opacity: [0, 1, 0],
                y: [0, 20 + i * 10],
                x: [(Math.random() - 0.5) * 30, (Math.random() - 0.5) * 60],
              }}
              transition={{
                duration: 0.8,
                delay: i * 0.1,
                repeat: Infinity,
                ease: 'easeOut',
              }}
            />
          ))}
        </motion.div>
      )}

      {/* Bubble propulsion effect */}
      {isMoving && (
        <motion.div
          style={{
            position: 'absolute',
            width: '120%',
            height: '120%',
            left: '-10%',
            top: '-10%',
            pointerEvents: 'none',
          }}
        >
          {[...Array(characterConfig.effects.bubbles.count)].map((_, i) => (
            <motion.div
              key={i}
              style={{
                position: 'absolute',
                width: characterConfig.effects.bubbles.size,
                height: characterConfig.effects.bubbles.size,
                left: `${
                  characterConfig.effects.bubbles.positions.min +
                  Math.random() * characterConfig.effects.bubbles.positions.max
                }%`,
                top: `${
                  characterConfig.effects.bubbles.positions.min +
                  Math.random() * characterConfig.effects.bubbles.positions.max
                }%`,
                borderRadius: '50%',
                background: characterConfig.colors.effects.bubble,
                border: characterConfig.colors.borders.bubble,
              }}
              animate={{
                scale: [0, 1.5, 0],
                y: [0, -30, -60],
                x: [0, (Math.random() - 0.5) * 20, (Math.random() - 0.5) * 40],
                opacity: [0, 0.8, 0],
              }}
              transition={{
                duration: 0.8,
                delay: i * 0.2,
                repeat: Infinity,
                ease: 'easeOut',
              }}
            />
          ))}
        </motion.div>
      )}

      {/* Wobble wrapper (tap feedback) */}
      <motion.div
        key={`wobble-${wobbleKey}`}
        style={{ width: '100%', height: '100%' }}
        {...(wobbleKey > 0 ? {
          initial: { rotate: 0, scale: 1 },
          animate: { rotate: [0, -12, 10, -8, 6, -3, 0], scale: [1, 1.12, 0.93, 1.06, 0.98, 1] },
          transition: { duration: 0.5, ease: 'easeOut' },
        } : {})}
      >

      {/* 3D Character Container */}
      <motion.div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          perspective: '500px',
        }}
        animate={{
          scale: deathPhase === 'impact' ? 1.3
            : deathPhase === 'fade' ? 0.6
            : teleportPhase === 'landing' ? 1.0
            : teleportPhase === 'suckIn' ? 0.05
            : teleportPhase === 'grow' ? 1.2
            : hasCollided ? 1.3
            : isJiggling ? 1.05
            : 1,
          rotateZ: deathPhase === 'deflate' ? 8
            : teleportPhase === 'landing' ? 0
            : teleportPhase === 'suckIn' ? 360
            : teleportPhase === 'grow' ? -10
            : isJiggling ? 3
            : 0,
          scaleX: deathPhase === 'deflate' ? 1.3
            : deathPhase === 'fade' ? 0.8
            : teleportPhase === 'landing' ? 1.0
            : teleportPhase === 'suckIn' ? 0.05
            : teleportPhase === 'grow' ? 1.15
            : isJiggling ? 0.95
            : 1,
          scaleY: deathPhase === 'deflate' ? 0.6
            : deathPhase === 'fade' ? 0.7
            : teleportPhase === 'landing' ? 0.92
            : teleportPhase === 'suckIn' ? 0.05
            : teleportPhase === 'grow' ? 1.15
            : isJiggling ? 1.05
            : 1,
          opacity: deathPhase === 'fade' ? 0.2
            : teleportPhase === 'suckIn' ? 0
            : 1,
          y: deathPhase === 'fade' ? 8
            : teleportPhase === 'landing' ? 4
            : teleportPhase === 'suckIn' ? 20
            : teleportPhase === 'grow' ? -12
            : 0,
          filter: deathPhase === 'fade' ? 'saturate(0.2) brightness(0.7)'
            : teleportPhase === 'landing' ? 'brightness(1.2) saturate(1.3)'
            : 'none',
          x: deathPhase === 'impact' ? [-4, 4, -3, 3, 0] : 0,
        }}
        transition={{
          scale: deathPhase !== 'none'
            ? { type: 'tween', duration: deathPhase === 'impact' ? 0.2 : 0.5, ease: 'easeOut' }
            : teleportPhase === 'landing'
            ? { type: 'tween', duration: 0.3, ease: 'easeOut' }
            : teleportPhase === 'suckIn'
            ? { type: 'tween', duration: 0.28, ease: [0.6, 0, 1, 1] }
            : teleportPhase === 'grow'
            ? { type: 'spring', stiffness: 500, damping: 14, mass: 0.4 }
            : hasCollided
            ? { type: 'spring', stiffness: 300, damping: 10, mass: 0.5 }
            : isJiggling
            ? { type: 'spring', stiffness: 200, damping: 8, mass: 0.3 }
            : undefined,
          rotateZ: deathPhase !== 'none'
            ? { type: 'tween', duration: 0.4, ease: 'easeOut' }
            : teleportPhase === 'suckIn'
            ? { type: 'tween', duration: 0.28, ease: [0.4, 0, 1, 1] }
            : teleportPhase === 'grow'
            ? { type: 'spring', stiffness: 300, damping: 12 }
            : teleportPhase !== 'idle'
            ? { type: 'tween', duration: 0.25, ease: 'easeInOut' }
            : isJiggling
            ? { type: 'spring', stiffness: 150, damping: 5, duration: 0.6 }
            : undefined,
          scaleX: deathPhase !== 'none'
            ? { type: 'tween', duration: 0.4, ease: 'easeOut' }
            : teleportPhase === 'suckIn'
            ? { type: 'tween', duration: 0.28, ease: [0.6, 0, 1, 1] }
            : teleportPhase === 'grow'
            ? { type: 'spring', stiffness: 500, damping: 14, mass: 0.4 }
            : teleportPhase !== 'idle'
            ? { type: 'tween', duration: 0.25, ease: 'easeInOut' }
            : isJiggling
            ? { type: 'spring', stiffness: 250, damping: 6 }
            : undefined,
          scaleY: deathPhase !== 'none'
            ? { type: 'tween', duration: 0.4, ease: 'easeOut' }
            : teleportPhase === 'landing'
            ? { type: 'tween', duration: 0.4, ease: [0.4, 0, 0.2, 1] }
            : teleportPhase === 'suckIn'
            ? { type: 'tween', duration: 0.28, ease: [0.6, 0, 1, 1] }
            : teleportPhase === 'grow'
            ? { type: 'spring', stiffness: 500, damping: 14, mass: 0.4 }
            : teleportPhase !== 'idle'
            ? { type: 'tween', duration: 0.25, ease: 'easeInOut' }
            : isJiggling
            ? { type: 'spring', stiffness: 250, damping: 6 }
            : undefined,
          opacity: deathPhase !== 'none'
            ? { type: 'tween', duration: 0.5, ease: 'easeOut' }
            : teleportPhase === 'suckIn'
            ? { type: 'tween', duration: 0.2, ease: 'easeIn' }
            : teleportPhase !== 'idle'
            ? { type: 'tween', duration: 0.15 }
            : undefined,
          y: deathPhase !== 'none'
            ? { type: 'tween', duration: 0.5, ease: 'easeOut' }
            : teleportPhase === 'landing'
            ? { type: 'tween', duration: 0.4, ease: [0.4, 0, 0.2, 1] }
            : teleportPhase === 'suckIn'
            ? { type: 'tween', duration: 0.28, ease: [0.6, 0, 1, 1] }
            : teleportPhase === 'grow'
            ? { type: 'spring', stiffness: 500, damping: 14, mass: 0.4 }
            : undefined,
          filter: deathPhase !== 'none'
            ? { type: 'tween', duration: 0.5 }
            : teleportPhase !== 'idle'
            ? { type: 'tween', duration: 0.2 }
            : undefined,
          x: deathPhase === 'impact'
            ? { type: 'tween', duration: 0.2, ease: 'easeOut' }
            : undefined,
        }}
      >
        <motion.div
          style={{
            width: cubeSize,
            height: cubeSize,
            position: 'relative',
            transformStyle: 'preserve-3d',
            transformOrigin: 'center center',
          }}
          animate={{
            rotateX:
              isMoving && (direction === 'up' || direction === 'down')
                ? direction === 'up'
                  ? -30
                  : 30
                : currentIdleAnimation === 'lookAround'
                ? [10, 0, 20, 10]
                : 10,
            rotateY:
              isMoving && (direction === 'left' || direction === 'right')
                ? direction === 'left'
                  ? 30
                  : -30
                : currentIdleAnimation === 'lookAround'
                ? [0, 20, -20, 0]
                : 0,
            rotateZ: currentIdleAnimation === 'dance' ? [0, -5, 5, -5, 0] : 0,
            y:
              currentIdleAnimation === 'bounce'
                ? [0, -10, 0]
                : currentIdleAnimation === 'breathe'
                ? [0, -3, 0]
                : 0,
            scaleX:
              isMoving && (direction === 'left' || direction === 'right')
                ? 1.2 // Stretch in direction of movement
                : isMoving && (direction === 'up' || direction === 'down')
                ? 0.9 // Compress in perpendicular axis
                : 1,
            scaleY:
              isMoving && (direction === 'up' || direction === 'down')
                ? 1.2 // Stretch in direction of movement
                : isMoving && (direction === 'left' || direction === 'right')
                ? 0.9 // Compress in perpendicular axis
                : 1,
            scaleZ: isMoving
              ? 0.95 // Slight depth compression for 3D effect
              : 1,
          }}
          transition={{
            rotateX: {
              type: isMoving ? 'spring' : 'tween',
              stiffness: isMoving ? 400 : undefined,
              damping: isMoving ? 30 : undefined,
              duration: isMoving ? undefined : 4,
              repeat: isMoving ? 0 : currentIdleAnimation === 'lookAround' ? Infinity : 0,
              ease: 'easeInOut',
            },
            rotateY: {
              type: isMoving ? 'spring' : 'tween',
              stiffness: isMoving ? 400 : undefined,
              damping: isMoving ? 30 : undefined,
              duration: isMoving ? undefined : 4,
              repeat: isMoving ? 0 : currentIdleAnimation === 'lookAround' ? Infinity : 0,
              ease: 'easeInOut',
            },
            rotateZ: {
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut',
            },
            y: {
              duration: currentIdleAnimation === 'bounce' ? 0.6 : 2,
              repeat: Infinity,
              ease: currentIdleAnimation === 'bounce' ? 'easeOut' : 'easeInOut',
            },
            scaleX: {
              type: 'spring',
              stiffness: isMoving ? 300 : 600,
              damping: isMoving ? 15 : 20,
              mass: 0.8,
            },
            scaleY: {
              type: 'spring',
              stiffness: isMoving ? 300 : 600,
              damping: isMoving ? 15 : 20,
              mass: 0.8,
            },
            scaleZ: {
              type: 'spring',
              stiffness: 500,
              damping: 20,
            },
          }}
        >
          {/* Front face */}
          <div
            style={{
              position: 'absolute',
              width: '101%',
              height: '101%',
              left: '-0.5%',
              top: '-0.5%',
              transform: `translateZ(${cubeSize / 4.5}px)`,
              background: characterConfig.colors.faces.front,
              borderRadius: '5%',
              border: '3px solid rgba(0, 0, 0, 0.3)',
              boxShadow:
                'inset 0 2px 4px rgba(255, 255, 255, 0.3), inset 0 -2px 4px rgba(0, 0, 0, 0.2)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8%',
              backfaceVisibility: 'hidden',
            }}
          >
            {/* Eyebrows */}
            <div
              style={{
                // display: 'flex',
                display: 'none',
                gap: characterConfig.face.eyes.gap,
                width: characterConfig.face.eyes.containerWidth,
                marginTop: '5%',
                marginBottom: '-5%',
                zIndex: 2,
              }}
            >
              {/* Left eyebrow */}
              <motion.div
                style={{
                  width: '40%',
                  height: '1px',
                  background: 'rgba(0, 0, 0, 0.6)',
                  borderRadius: '2px',
                  transformOrigin: 'right center',
                }}
                animate={{
                  rotate:
                    expression === 'shocked'
                      ? -15
                      : expression === 'sad'
                      ? 10
                      : currentIdleAnimation === 'lookAround'
                      ? [0, -5, 5, 0]
                      : expression === 'wink'
                      ? -8
                      : isMoving && direction === 'up'
                      ? 5
                      : isMoving && direction === 'down'
                      ? -5
                      : 0,
                  y:
                    expression === 'shocked'
                      ? -3
                      : currentIdleAnimation === 'bounce'
                      ? [0, -1, 0]
                      : expression === 'happy'
                      ? -1
                      : isMoving
                      ? -2
                      : 0,
                  scaleX: currentIdleAnimation === 'dance' ? [1, 0.9, 1] : isMoving ? 1.1 : 1,
                }}
                transition={{
                  rotate: {
                    duration: currentIdleAnimation === 'lookAround' ? 4 : isMoving ? 0.3 : 0.2,
                    repeat: currentIdleAnimation === 'lookAround' ? Infinity : 0,
                    ease: 'easeInOut',
                  },
                  y: {
                    duration: currentIdleAnimation === 'bounce' ? 0.6 : 0.2,
                    repeat: currentIdleAnimation === 'bounce' ? Infinity : 0,
                    ease: 'easeOut',
                  },
                  scaleX: {
                    duration: 0.5,
                    repeat: currentIdleAnimation === 'dance' ? Infinity : 0,
                    ease: 'easeInOut',
                  },
                }}
              />

              {/* Right eyebrow */}
              <motion.div
                style={{
                  width: '40%',
                  height: '1px',
                  background: 'rgba(0, 0, 0, 0.6)',
                  borderRadius: '2px',
                  transformOrigin: 'left center',
                }}
                animate={{
                  rotate:
                    expression === 'shocked'
                      ? 15
                      : expression === 'sad'
                      ? -10
                      : currentIdleAnimation === 'lookAround'
                      ? [0, 5, -5, 0]
                      : expression === 'wink'
                      ? 0
                      : isMoving && direction === 'up'
                      ? -5
                      : isMoving && direction === 'down'
                      ? 5
                      : 0,
                  y:
                    expression === 'shocked'
                      ? -3
                      : currentIdleAnimation === 'bounce'
                      ? [0, -1, 0]
                      : expression === 'happy'
                      ? -1
                      : isMoving
                      ? -2
                      : 0,
                  scaleX: currentIdleAnimation === 'dance' ? [1, 0.9, 1] : isMoving ? 1.1 : 1,
                }}
                transition={{
                  rotate: {
                    duration: currentIdleAnimation === 'lookAround' ? 4 : isMoving ? 0.3 : 0.2,
                    repeat: currentIdleAnimation === 'lookAround' ? Infinity : 0,
                    ease: 'easeInOut',
                  },
                  y: {
                    duration: currentIdleAnimation === 'bounce' ? 0.6 : 0.2,
                    repeat: currentIdleAnimation === 'bounce' ? Infinity : 0,
                    ease: 'easeOut',
                  },
                  scaleX: {
                    duration: 0.5,
                    repeat: currentIdleAnimation === 'dance' ? Infinity : 0,
                    ease: 'easeInOut',
                  },
                }}
              />
            </div>

            {/* Eyes */}
            <div
              style={{
                display: 'flex',
                gap: characterConfig.face.eyes.gap,
                width: characterConfig.face.eyes.containerWidth,
                marginTop: characterConfig.face.eyes.containerMarginTop,
                marginLeft: '7%',
              }}
            >
              {/* Left eye */}
              <motion.div
                style={{
                  width: characterConfig.face.eyes.size,
                  aspectRatio: '1',
                  background: characterConfig.face.eyes.color,
                  borderRadius: expression === 'wink' ? '50% 50% 50% 20%' : '50%',
                  position: 'relative',
                }}
                animate={{
                  scaleY: (deathPhase === 'deflate' || deathPhase === 'fade')
                    ? 0.05
                    : isBlinking || expression === 'wink' ? 0.1
                    : expression === 'shocked' ? 1.2
                    : 1,
                }}
                transition={{ duration: 0.1 }}
              >
                {!isBlinking && expression !== 'wink' && deathPhase !== 'deflate' && deathPhase !== 'fade' && (
                  <div
                    style={{
                      position: 'absolute',
                      top: characterConfig.face.eyes.shine.position.top,
                      left: characterConfig.face.eyes.shine.position.left,
                      width: characterConfig.face.eyes.shine.size,
                      height: characterConfig.face.eyes.shine.size,
                      background: characterConfig.face.eyes.shine.color,
                      borderRadius: '50%',
                    }}
                  />
                )}
              </motion.div>

              {/* Right eye */}
              <motion.div
                style={{
                  width: characterConfig.face.eyes.size,
                  aspectRatio: '1',
                  background: characterConfig.face.eyes.color,
                  borderRadius: '50%',
                  position: 'relative',
                }}
                animate={{
                  scaleY: (deathPhase === 'deflate' || deathPhase === 'fade')
                    ? 0.05
                    : isBlinking ? 0.1
                    : expression === 'shocked' ? 1.2
                    : 1,
                }}
                transition={{ duration: 0.1 }}
              >
                {!isBlinking && deathPhase !== 'deflate' && deathPhase !== 'fade' && (
                  <div
                    style={{
                      position: 'absolute',
                      top: characterConfig.face.eyes.shine.position.top,
                      left: characterConfig.face.eyes.shine.position.left,
                      width: characterConfig.face.eyes.shine.size,
                      height: characterConfig.face.eyes.shine.size,
                      background: characterConfig.face.eyes.shine.color,
                      borderRadius: '50%',
                    }}
                  />
                )}
              </motion.div>
            </div>

            {/* Mouth */}
            <motion.div
              style={{
                width: characterConfig.face.mouth.width,
                height:
                  expression === 'happy'
                    ? characterConfig.face.mouth.heights.happy
                    : expression === 'shocked'
                    ? characterConfig.face.mouth.heights.shocked
                    : characterConfig.face.mouth.heights.neutral,
                background: characterConfig.face.mouth.color,
                borderRadius:
                  expression === 'happy'
                    ? '10% 10% 50% 50%'
                    : expression === 'sad'
                    ? '50% 50% 20% 20%'
                    : expression === 'shocked'
                    ? '50%'
                    : '50%',
                position: 'relative',
                overflow: 'hidden',
                marginTop: '5%',
                marginLeft: '21%',
              }}
              animate={{
                width: expression === 'shocked' ? '40%' : characterConfig.face.mouth.width,
                scale: currentIdleAnimation === 'dance' ? [1, 1.1, 1] : 1,
              }}
              transition={{
                width: { duration: 0.2 },
                scale: { duration: 0.5, repeat: Infinity, ease: 'easeInOut' },
              }}
            >
              {/* Teeth for happy expression */}
              {expression === 'happy' && (
                <div
                  style={{
                    position: 'absolute',
                    top: '0',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '80%',
                    height: '30%',
                    background: 'rgba(255, 255, 255, 0.8)',
                    borderRadius: '0 0 50% 50%',
                  }}
                />
              )}

              {/* Tongue for dance animation */}
              {currentIdleAnimation === 'dance' && (
                <motion.div
                  style={{
                    position: 'absolute',
                    bottom: '-20%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '60%',
                    height: '40%',
                    background: 'rgba(255, 100, 100, 0.6)',
                    borderRadius: '50% 50% 50% 50%',
                  }}
                  animate={{
                    bottom: ['-20%', '0%', '-20%'],
                  }}
                  transition={{
                    duration: 1,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                />
              )}
            </motion.div>
          </div>

          {/* Back face */}
          <div
            style={{
              position: 'absolute',
              width: '101%',
              height: '101%',
              left: '-0.5%',
              top: '-0.5%',
              transform: `translateZ(-${cubeSize / 2}px) rotateY(180deg)`,
              background: characterConfig.colors.faces.back,
              borderRadius: '15%',
              border: '3px solid rgba(0, 0, 0, 0.3)',
              boxShadow:
                'inset 0 2px 4px rgba(255, 255, 255, 0.3), inset 0 -2px 4px rgba(0, 0, 0, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backfaceVisibility: 'hidden',
            }}
          >
            <div
              style={{
                fontSize: `${cubeSize * characterConfig.backFace.sizeScale}px`,
                opacity: characterConfig.backFace.opacity,
              }}
            >
              {characterConfig.backFace.symbol}
            </div>
          </div>

          {/* Top face */}
          <div
            style={{
              position: 'absolute',
              width: '101%',
              height: '50%',
              left: '-0.5%',
              top: '25%',
              transform: `translateY(-${cubeSize / 2}px) rotateX(90deg)`,
              background: characterConfig.colors.faces.top,
              borderRadius: '5%',
              border: '3px solid rgba(0, 0, 0, 0.3)',
              boxShadow:
                'inset 0 2px 4px rgba(255, 255, 255, 0.3), inset 0 -2px 4px rgba(0, 0, 0, 0.2)',
              backfaceVisibility: 'hidden',
            }}
          />

          {/* Bottom face */}
          <div
            style={{
              position: 'absolute',
              width: '99%',
              height: '50%',
              left: '0.5%',
              top: '25%',
              transform: `translateY(${cubeSize / 2}px) rotateX(-90deg)`,
              background: characterConfig.colors.faces.bottom,
              borderRadius: '5%',
              border: '3px solid rgba(0, 0, 0, 0.3)',
              boxShadow:
                'inset 0 2px 4px rgba(255, 255, 255, 0.3), inset 0 -2px 4px rgba(0, 0, 0, 0.2)',
              backfaceVisibility: 'hidden',
            }}
          />

          {/* Left face */}
          <div
            style={{
              position: 'absolute',
              width: '50%',
              height: '101%',
              left: '-0.5%',
              top: '-0.5%',
              transform: `translateX(-${cubeSize / 4}px) rotateY(-90deg)`,
              background: characterConfig.colors.faces.left,
              borderRadius: '5%',
              border: '3px solid rgba(0, 0, 0, 0.3)',
              boxShadow:
                'inset 0 2px 4px rgba(255, 255, 255, 0.3), inset 0 -2px 4px rgba(0, 0, 0, 0.2)',
              backfaceVisibility: 'hidden',
            }}
          />

          {/* Right face */}
          <div
            style={{
              position: 'absolute',
              width: '50%',
              height: '101%',
              left: '50%',
              top: '-0.5%',
              transform: `translateX(${cubeSize / 4}px) rotateY(90deg)`,
              background: characterConfig.colors.faces.right,
              borderRadius: '5%',
              border: '3px solid rgba(0, 0, 0, 0.3)',
              boxShadow:
                'inset 0 2px 4px rgba(255, 255, 255, 0.3), inset 0 -2px 4px rgba(0, 0, 0, 0.2)',
              backfaceVisibility: 'hidden',
            }}
          />
        </motion.div>
        
        {/* Render equipped props */}
        {equippedProps.map(prop => (
          <Prop3D
            key={prop.id}
            prop={prop}
            size={cubeSize}
            isEquipped={true}
          />
        ))}
      </motion.div>

      {/* Shadow */}
      <motion.div
        style={{
          position: 'absolute',
          width: `${characterConfig.sizes.shadow.width * 100}%`,
          height: `${characterConfig.sizes.shadow.height * 100}%`,
          bottom: `-${cellSize * characterConfig.sizes.shadow.offsetY}px`,
          left: `${(1 - characterConfig.sizes.shadow.width) * 50}%`,
          background: characterConfig.colors.effects.shadow,
          borderRadius: '50%',
          transform: `scaleY(0.5) translateY(${elevation}px)`,
        }}
        animate={{
          scaleX: isMoving ? 1.2 : 1,
          opacity: isMoving ? 0.5 : 1,
        }}
        transition={{
          duration: 0.3,
        }}
      />

      {/* Bubble propulsion trail */}
      {previousPositions.map(({ position: bubblePos, id }) => (
        <motion.div
          key={id}
          style={{
            position: 'absolute',
            left: bubblePos[0] * cellSize + cellSize / 2,
            top: bubblePos[1] * cellSize + cellSize / 2,
            width: cellSize * 0.6,
            height: cellSize * 0.6,
            marginLeft: -cellSize * 0.3,
            marginTop: -cellSize * 0.3,
            pointerEvents: 'none',
            zIndex: 900,
          }}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: [0, 1.2, 0.8], opacity: [0, 0.6, 0] }}
          transition={{
            duration: 0.8,
            ease: 'easeOut',
          }}
        >
          {/* Multiple bubble layers for depth */}
          <div
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              background:
                'radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.8) 0%, rgba(100, 200, 255, 0.4) 40%, transparent 70%)',
              borderRadius: '50%',
              filter: 'blur(1px)',
            }}
          />
          <div
            style={{
              position: 'absolute',
              width: '80%',
              height: '80%',
              left: '10%',
              top: '10%',
              background:
                'radial-gradient(circle at 40% 40%, rgba(200, 230, 255, 0.6) 0%, rgba(100, 200, 255, 0.3) 50%, transparent 80%)',
              borderRadius: '50%',
              transform: 'rotate(45deg)',
            }}
          />
          <div
            style={{
              position: 'absolute',
              width: '60%',
              height: '60%',
              left: '20%',
              top: '20%',
              border: '2px solid rgba(100, 200, 255, 0.3)',
              borderRadius: '50%',
              boxShadow: 'inset 0 0 10px rgba(255, 255, 255, 0.3)',
            }}
          />
        </motion.div>
      ))}

      </motion.div>{/* end wobble wrapper */}
    </motion.div>
  );
};

export default Character3D;
